import { useTable } from '@refinedev/antd';
import type { useTableProps, useTableReturnType } from '@refinedev/antd';
import type { BaseRecord, HttpError } from '@refinedev/core';

/**
 * `useTable` with antd's filter state disconnected.
 *
 * Filters in this app are owned by `setFilters` (see the components in
 * `src/components/common/*Filter.tsx`); antd only renders the dropdowns and the
 * active-funnel highlight. antd still ships its own filter map to `onChange` on
 * every sort and pagination change, and Refine would map those (all-null)
 * entries into CRUD filters and merge them, wiping the real ones. Blanking the
 * map makes Refine skip the mapping entirely — it guards on
 * `Object.keys(tableFilters).length > 0`.
 */
export const useAdminTable = <
  TQueryFnData extends BaseRecord = BaseRecord,
  TError extends HttpError = HttpError,
  TSearchVariables = unknown,
  TData extends BaseRecord = TQueryFnData,
>(
  props?: useTableProps<TQueryFnData, TError, TSearchVariables, TData>
): useTableReturnType<TData, TError, TSearchVariables> => {
  const result = useTable<TQueryFnData, TError, TSearchVariables, TData>(props);
  const { onChange } = result.tableProps;

  return {
    ...result,
    tableProps: {
      ...result.tableProps,
      onChange: (pagination, _filters, sorter, extra) =>
        onChange?.(pagination, {}, sorter, extra),
    },
  };
};
